<script setup lang="ts">
// Right-side drawer mounted on row click. View-only — read the operator
// record at a glance. All mutations (edit / suspend / reset MFA / resend
// invite / remove) live on the row's kebab menu in AdminsTable; the
// drawer doesn't duplicate them.
//
// Layout:
//   1. Sticky header — avatar identity + close
//   2. Status chips row
//   3. Security section
//   4. Account info section

import { computed } from 'vue'

import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Drawer from '@uiKit/elements/Drawer.vue'
import Chip from '@uiKit/elements/Chip.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import DetailRow from '@uiKit/components/DetailRow.vue'
import Avatar from '@uiKit/components/Avatar.vue'
import DrawerHeader from '@uiKit/components/DrawerHeader.vue'
import { formatDateTime } from '@shared/utils'

import type { Admin, AdminStatus, InvitationStatus } from '../types/adminTypes'

interface Props {
  visible: boolean
  admin: Admin | null
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
}>()

const formatOrDash = (iso: string | null) => (iso ? formatDateTime(iso) : '—')

type ChipColor = 'success' | 'warning' | 'danger' | 'info' | 'neutral'

const STATUS_COLOR: Record<AdminStatus, ChipColor> = {
  Active: 'success',
  Inactive: 'neutral',
  Suspended: 'danger',
}

const INVITATION_LABEL: Record<InvitationStatus, string> = {
  invited: 'Invite pending',
  accepted: 'Accepted',
  expired: 'Invite expired',
  revoked: 'Invite revoked',
}

const INVITATION_COLOR: Record<InvitationStatus, ChipColor> = {
  invited: 'info',
  accepted: 'success',
  expired: 'warning',
  revoked: 'danger',
}

const statusColor = computed<ChipColor>(() =>
  props.admin ? STATUS_COLOR[props.admin.status] : 'neutral',
)

const closeDrawer = () => emit('update:visible', false)
</script>

<template>
  <Drawer
    :model-value="visible"
    position="right"
    width="520px"
    :show-close-icon="false"
    @update:model-value="emit('update:visible', $event)"
  >
    <template #header>
      <DrawerHeader v-if="admin" :title="admin.name" :subtitle="admin.email" @close="closeDrawer">
        <template #avatar>
          <Avatar :id="admin.id" :name="admin.name" size="md" />
        </template>
        <template #extras>
          <Box
            v-if="admin.role === 'Super Admin'"
            flex
            align="center"
            justify="center"
            class="super-pin"
            aria-label="Super admin"
          >
            <PiIcon name="shield" />
          </Box>
        </template>
      </DrawerHeader>
    </template>

    <Box v-if="admin" flex direction="col" class="detail-body">
      <!-- ── Status row ──────────────────────────────────────────── -->
      <Box flex align="center" gap="2" class="detail-chips">
        <Chip :label="admin.status" :color="statusColor" variant="tonal" size="small" dot />
        <Chip :label="admin.role" color="neutral" variant="tonal" size="small" />
        <Chip
          v-if="admin.invitationStatus !== 'accepted'"
          :label="INVITATION_LABEL[admin.invitationStatus]"
          :color="INVITATION_COLOR[admin.invitationStatus]"
          variant="tonal"
          size="small"
          icon="envelope"
        />
        <Chip
          :label="admin.mfaEnabled ? 'MFA on' : 'MFA off'"
          :color="admin.mfaEnabled ? 'success' : 'warning'"
          variant="tonal"
          size="small"
          :icon="admin.mfaEnabled ? 'shield' : 'exclamation-triangle'"
        />
      </Box>

      <!-- ── Security section ────────────────────────────────────── -->
      <Box flex direction="col" gap="3" class="detail-section">
        <Text type="caption-sm" transform="uppercase" weight="semibold" color="muted"
          >Security</Text
        >
        <Box flex direction="col" class="detail-rows">
          <DetailRow label="Multi-factor auth" :value="admin.mfaEnabled ? 'Enabled' : 'Disabled'" />
          <DetailRow label="Access scope" :value="admin.accessScope" />
          <DetailRow label="Last login" :value="formatOrDash(admin.lastLoginAt)" />
          <DetailRow
            label="Last permission change"
            :value="formatOrDash(admin.lastPermissionChangeAt)"
          />
        </Box>
      </Box>

      <!-- ── Account info section ────────────────────────────────── -->
      <Box flex direction="col" gap="3" class="detail-section detail-section--last">
        <Text type="caption-sm" transform="uppercase" weight="semibold" color="muted">Account</Text>
        <Box flex direction="col" class="detail-rows">
          <DetailRow label="Created by" :value="admin.createdBy" />
          <DetailRow label="Created at" :value="formatOrDash(admin.createdAt)" />
          <DetailRow label="Invitation" :value="INVITATION_LABEL[admin.invitationStatus]" />
        </Box>
      </Box>
    </Box>
  </Drawer>
</template>

<style scoped>
/* Super-admin role pin shown inline next to the title */
.super-pin {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background: var(--p-primary-500);
  color: var(--p-primary-contrast-color);
  flex-shrink: 0;
}

/* ── Body ── */
.detail-body {
  padding: 0;
}

.detail-chips {
  padding: 20px 24px;
  flex-wrap: wrap;
  border-bottom: 1px solid rgba(15, 23, 42, 0.05);
}

.detail-section {
  padding: 20px 24px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.05);
}

.detail-section--last {
  border-bottom: none;
  padding-bottom: 32px;
}

/* DetailRow list — flat vertical rhythm with hairline between rows. */
.detail-rows :deep(.detail-row) {
  padding: 10px 0;
  border-bottom: 1px solid rgba(15, 23, 42, 0.04);
}

.detail-rows :deep(.detail-row:last-child) {
  border-bottom: none;
}
</style>
